/**
 * Plain-word messages for Firebase Auth errors raised while setting up
 * two-factor authentication (#943). Used by My Account › Security and by
 * /enroll-mfa so neither page ever shows Firebase's raw `error.message`
 * ("Firebase: Error (auth/unverified-email).").
 */

/** What a page should show for one enrollment error. */
export interface MfaErrorDisplay {
  /** Sentence to show the user. */
  message: string;
  /** True when the user must sign in again before retrying. */
  requiresReauth: boolean;
  /** True when the user must verify their email before retrying. */
  requiresVerifiedEmail: boolean;
}

/** Shown for any code not listed below. */
export const MFA_DEFAULT_ERROR = 'Something went wrong. Try again.';

/** Message when Firebase refuses enrollment for an unverified address. */
export const MFA_UNVERIFIED_EMAIL_MESSAGE =
  'Verify your email to turn on two-factor authentication.';

const MESSAGES: Readonly<Record<string, string>> = {
  'auth/unverified-email': MFA_UNVERIFIED_EMAIL_MESSAGE,
  'auth/requires-recent-login':
    'For your security, sign in again before changing two-factor authentication.',
  'auth/operation-not-allowed':
    "This sign-in method isn't enabled for two-factor authentication yet. Contact support.",
  'auth/admin-restricted-operation':
    "This sign-in method isn't enabled for two-factor authentication yet. Contact support.",
  'auth/invalid-verification-code': "That code isn't right. Check it and try again.",
  'auth/missing-verification-code': 'Enter the code to continue.',
  'auth/code-expired': 'That code has expired. Request a new one.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
  'auth/captcha-check-failed': 'The security check expired. Try again.',
  'auth/invalid-app-credential': 'The security check expired. Try again.',
  'auth/invalid-phone-number':
    "That phone number doesn't look right. Include the country code, for example +15551234567.",
  'auth/missing-phone-number': 'Enter a phone number, including the country code.',
  'auth/second-factor-already-in-use':
    "That method is already set up on your account.",
  'auth/maximum-second-factor-count-exceeded':
    "You've reached the limit of two-factor methods. Remove one before adding another.",
  'auth/quota-exceeded': "We can't send text messages right now. Try again later.",
  'auth/network-request-failed': "We couldn't reach the server. Check your connection and try again.",
};

/**
 * Maps a Firebase Auth error (or its code) to the message to show.
 *
 * @param error - The caught error, or a bare `auth/...` code.
 * @returns The message and whether re-authentication or verification is needed.
 */
export function mfaErrorDisplay(error: unknown): MfaErrorDisplay {
  const code =
    typeof error === 'string'
      ? error
      : ((error as { code?: unknown } | null)?.code as string | undefined) ?? '';
  return {
    message: MESSAGES[code] ?? MFA_DEFAULT_ERROR,
    requiresReauth: code === 'auth/requires-recent-login',
    requiresVerifiedEmail: code === 'auth/unverified-email',
  };
}

/**
 * The Firebase error code, for logging (never the message, which can carry
 * a phone number or email address).
 *
 * @param error - The caught error.
 * @returns The `auth/...` code, or "unknown".
 */
export function mfaErrorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code : 'unknown';
}
